import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const AREAS = [
  {
    city: 'Oakville',
    icon: 'ri-building-4-line',
    phone: '(905) 845-4009',
    tel: '9058454009',
    desc: 'Our head office. Local specialists in condo HVAC regulations and older home heating conversions throughout all Oakville neighbourhoods.',
    highlight: 'Head Office',
    tag: 'GTA West',
  },
  {
    city: 'Burlington',
    icon: 'ri-home-8-line',
    phone: '(905) 845-4009',
    tel: '9058454009',
    desc: 'Full residential and commercial HVAC service across Burlington — from downtown to the waterfront and Aldershot communities.',
    highlight: 'GTA West',
    tag: 'GTA West',
  },
  {
    city: 'Mississauga',
    icon: 'ri-building-2-line',
    phone: '(905) 845-4009',
    tel: '9058454009',
    desc: 'Expert heating, cooling, and air quality installations and repairs for Mississauga homeowners and businesses of all sizes.',
    highlight: 'GTA West',
    tag: 'GTA West',
  },
  {
    city: 'Toronto',
    icon: 'ri-community-line',
    phone: '(416) 891-3444',
    tel: '4168913444',
    desc: 'Urban HVAC expertise for Toronto\'s high-density condos, older city homes, and commercial properties throughout the city.',
    highlight: 'GTA East',
    tag: 'GTA East',
  },
  {
    city: 'Etobicoke',
    icon: 'ri-home-gear-line',
    phone: '(416) 891-3444',
    tel: '4168913444',
    desc: 'Complete heating and cooling solutions for Etobicoke\'s diverse residential and commercial properties of all sizes.',
    highlight: 'GTA East',
    tag: 'GTA East',
  },
  {
    city: 'Waterdown',
    icon: 'ri-landscape-line',
    phone: '(905) 845-4009',
    tel: '9058454009',
    desc: 'Reliable HVAC service for the growing Waterdown community and surrounding Hamilton region.',
    highlight: 'GTA West',
    tag: 'GTA West',
  },
];

export default function ServiceAreas() {
  const { ref, visible } = useScrollReveal<HTMLElement>();

  return (
    <section ref={ref} className="bg-luxury-cream py-28 lg:py-36 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-14">

        {/* Header */}
        <div
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-0.5 bg-luxury-gold-dark" />
              <span className="font-caps text-[10px] tracking-[0.3em] uppercase text-luxury-text-muted">Where We Serve</span>
            </div>
            <h2 className="font-serif text-5xl lg:text-7xl text-luxury-text-dark leading-[1.0]">
              Serving the Greater<br />
              <em className="not-italic text-luxury-gold-dark">Toronto Area</em>
            </h2>
          </div>
          <div className="max-w-xs">
            <p className="font-sans text-sm text-luxury-text-muted leading-relaxed">
              Two dedicated teams — GTA West and GTA East — delivering faster response times and deep local expertise.
            </p>
          </div>
        </div>

        {/* Map + Cities Grid */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Embedded Google Map */}
          <div className="lg:col-span-5 rounded-2xl overflow-hidden border border-luxury-gold/15 relative bg-luxury-dark min-h-[420px]">
            <iframe
              title="Atlantis HVAC Systems — Oakville Head Office"
              src="https://maps.google.com/maps?q=1200+Speers+Road+Oakville+ON+L6L+2X4&output=embed&z=13"
              className="w-full h-full min-h-[420px] border-0"
              loading="lazy"
              allowFullScreen
              style={{ filter: 'grayscale(20%) contrast(105%)' }}
            />
            {/* Map info overlay */}
            <div className="absolute bottom-5 left-5 glass-dark border border-luxury-gold/22 rounded-xl px-4 py-3">
              <span className="font-caps text-[9px] tracking-[0.25em] uppercase text-luxury-gold block whitespace-nowrap">Head Office</span>
              <p className="font-sans text-xs text-white whitespace-nowrap mt-0.5">6-1200 Speers Rd, Oakville, ON</p>
            </div>
          </div>

          {/* City Cards */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {AREAS.map((area, i) => (
              <div
                key={area.city}
                className={`group bg-white rounded-xl p-5 border border-gray-100 hover:border-luxury-gold/30 transition-all duration-300 hover:-translate-y-0.5 shadow-luxury cursor-default ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: visible ? `${200 + i * 75}ms` : '0ms' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-luxury-warm border border-luxury-gold/18 group-hover:bg-luxury-gold/10 transition-colors">
                    <i className={`${area.icon} text-luxury-gold-dark text-base`} />
                  </div>
                  <span
                    className="font-caps text-[9px] tracking-widest uppercase px-2.5 py-1 rounded-full whitespace-nowrap border text-luxury-gold-dark/70 border-luxury-gold/20 bg-luxury-gold/5"
                  >
                    {area.highlight}
                  </span>
                </div>
                <h3 className="font-serif text-luxury-text-dark text-xl font-semibold mb-1.5 group-hover:text-luxury-gold-dark transition-colors">
                  {area.city}
                </h3>
                <p className="font-sans text-xs text-luxury-text-muted leading-relaxed mb-3">{area.desc}</p>
                <a
                  href={`tel:${area.tel}`}
                  className="inline-flex items-center gap-1.5 font-caps text-[9px] tracking-[0.2em] uppercase text-luxury-gold-dark/70 hover:text-luxury-gold-dark transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xs" />
                  <span>{area.phone}</span>
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Footer CTA */}
        <div
          className={`text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '500ms' }}
        >
          <Link
            to="/locations"
            className="inline-flex items-center gap-3 bg-luxury-gold hover:bg-luxury-gold-light text-white font-caps text-xs tracking-[0.15em] uppercase px-10 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap shadow-gold"
          >
            <span>View All Service Areas</span>
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
